/** @jsxImportSource @emotion/react */

import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Content from '@component/common/content';
import * as size from '@constant/size';
import * as color from '@constant/color';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import SideTab from '@component/iyagigo/navigator/side-tab';
import BottomTab from '@component/iyagigo/navigator/bottom-tab';
import IconBlock from '@component/common/block/icon-block';
import HeaderContainer from '@component/common/architecture/header/header-container';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import { actions as navigationActions } from '@store/slice/navigation-slice';
import { Helmet } from 'react-helmet';
import { useAuthPage } from '@hook/auth/useAuth';
import TextBlock from '@component/common/block/text-block';
import Callout from '@design-system/callout';
import { ETheme } from '@datatype/type';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { actions as modalActions } from '@store/slice/modal-slice';
import { IS_DEMO } from '@constant/variable';

interface IProps {
  title: string;
  mode: 'page' | 'back' | 'close';
  children: React.ReactNode;
  handleNavigationClick?: () => void;
  customHeader?: React.ReactNode;
  backgroundColor?: string;
  hideSignin?: boolean;
}

const HEADER_ICON_WIDTH = '30px';

const HEADER_ICON_DESIGN = {
  icon: { size: 'medium' },
  box: { width: HEADER_ICON_WIDTH, left: '15px', event: { cursor: 'pointer' } },
} as const;

const HEADER_CONTAINER_DESIGN = {
  left: IS_IYAGIGO_MOBILE_VIEW ? '0px' : size.WEB_SIDE_WIDTH,
  right: '0px',
  height: size.MOBILE_HEADER_HEIGHT,
  align: IS_IYAGIGO_MOBILE_VIEW ? 'around' : 'right',
  // backgroundColor: color.GRAY_4,
  backgroundColor: color.WHITE_1,
  boxShadow: color.HEADER_SHADOW,
  zIndex: 10,
} as const;

const HEADER_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2 },
  box: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
} as const;

const SIGNIN_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
  box: { event: { cursor: 'pointer' }, right: '15px' },
} as const;

// const ISLAND_MARGIN = '25px';
const ISLAND_MARGIN = '0px';

const Island = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: ${size.ISLAND_WIDTH};
  height: ${`calc(100% - 2 * ${ISLAND_MARGIN})`};
  margin: ${ISLAND_MARGIN} auto 0 auto;
  // background-color: ${color.WHITE_1};
  // box-shadow: ${process.env.NODE_ENV === 'production' ? 'none' : color.SHEET_SHADOW};
  // border-radius: ${size.BORDER_RADIUS_1};
`;

const CustomHeaderContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  width: calc(100% - 2 * 80px);
`;

const CalloutContainer = styled.div`
  position: absolute;
  left: 55px;
  z-index: 2;
`;

const PageTemplate: React.FC<IProps> = ({
  title,
  children,
  mode,
  handleNavigationClick,
  customHeader,
  backgroundColor,
  hideSignin = false,
}) => {
  // ############### state ###############
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const isMobileSideTab = useTypedSelector((state) => state.rootReducer?.navigationReducer?.isMobileSideTab) ?? false;
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const isCallout = useTypedSelector((state) => state.rootReducer?.modalReducer?.isCallout) ?? false;

  // ############### api ###############

  // ############### handler ###############
  const handleMenuClick = () => {
    if (handleNavigationClick) return handleNavigationClick();
    dispatch(navigationActions.openMobileSideTab());
    dispatch(modalActions.hideOverlay());
  };
  const handleSigninClick = () => openAuthPage('signin');

  // ############### etc ###############
  const { openAuthPage } = useAuthPage();
  const isHeaderTitleVisible = !(IS_DEMO && mode === 'page');

  return (
    <>
      <Helmet>
        <title>{`${title} - 이야기고`}</title>
      </Helmet>
      {IS_IYAGIGO_MOBILE_VIEW ? (
        <>
          {isMobileSideTab && <SideTab />}
          {customHeader !== 'none' && (
            <HeaderContainer design={HEADER_CONTAINER_DESIGN}>
              <IconBlock
                icon={mode === 'page' ? 'menu' : mode === 'close' ? 'close' : 'back'}
                design={HEADER_ICON_DESIGN}
                click={{ handler: handleMenuClick }}
              />
              {/*{!isMobileSideTab && isCallout && (*/}
              {/*  <CalloutContainer>*/}
              {/*    <Callout*/}
              {/*      text='여기서 학습언어를 바꿀 수 있어요'*/}
              {/*      direction='left'*/}
              {/*      size='small'*/}
              {/*      theme={ETheme.BROWN}*/}
              {/*      animation*/}
              {/*    />*/}
              {/*  </CalloutContainer>*/}
              {/*)}*/}
              {customHeader ? (
                <CustomHeaderContainer>{customHeader}</CustomHeaderContainer>
              ) : isHeaderTitleVisible ? (
                <TextBlock text={title} design={HEADER_TEXT_DESIGN} />
              ) : null}
              {!isAuthenticated && !hideSignin && (
                <TextBlock text={`로그인`} design={SIGNIN_TEXT_DESIGN} click={{ handler: handleSigninClick }} />
              )}
            </HeaderContainer>
          )}
          <Content
            top={customHeader === 'none' ? 'var(--sat)' : `${size.MOBILE_HEADER_HEIGHT} + var(--sat)`}
            bottom={mode === 'page' ? `${size.MOBILE_BOTTOM_HEIGHT} + var(--sab)` : 'var(--sab)'}
            backgroundColor={backgroundColor}>
            {children}
          </Content>
          {mode === 'page' && <BottomTab />}
        </>
      ) : (
        <>
          <SideTab />
          {customHeader !== 'none' && (
            <HeaderContainer design={HEADER_CONTAINER_DESIGN}>
              {mode !== 'page' && (
                <IconBlock
                  icon={mode === 'close' ? 'close' : 'back'}
                  design={HEADER_ICON_DESIGN}
                  click={{ handler: handleMenuClick }}
                />
              )}
              {customHeader ? (
                <CustomHeaderContainer>{customHeader}</CustomHeaderContainer>
              ) : isHeaderTitleVisible ? (
                <TextBlock text={title} design={HEADER_TEXT_DESIGN} />
              ) : null}
              {!isAuthenticated && !hideSignin && (
                <TextBlock text={`로그인`} design={SIGNIN_TEXT_DESIGN} click={{ handler: handleSigninClick }} />
              )}
            </HeaderContainer>
          )}
          <Content
            left={size.WEB_SIDE_WIDTH}
            top={customHeader === 'none' ? '0px' : size.WEB_HEADER_HEIGHT}
            bottom='0px'
            backgroundColor={backgroundColor}>
            <Island>{children}</Island>
          </Content>
        </>
      )}
    </>
  );
};

export default PageTemplate;
